import React from 'react';
import { inject, observer } from 'mobx-react';
import { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { Flex, Link } from 'core/components';
import IdTag, { formatDisplayId } from 'app/components/IdTag';
import LabelValue from 'app/views/metrics/components/LabelValue';
import AlertSeverityBars from 'app/views/alerting/components/AlertSeverityBars';
import { formatDateTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS } from 'core/util/dateUtils';
import { ALERT_CLEAR_REASONS } from '@kentik/ui-shared/alerting/constants';
import storeLoader from 'app/stores/storeLoader';
import { correlationPath } from 'app/views/alerting/correlations/links';
import AlertStatusTag from '../AlertStatusTag';

/**
 * This component displays the following information about an Alert:
 * - Tenant link (if tenant properties are specified)
 * - Alert ID
 * - Severity
 * - Alert state
 * - Ack state
 * - Alert start and end
 * - Clear status
 * - AI correlation link (if the user can view correlations)
 */

@withTheme
@storeLoader('$users.collection')
@inject('$auth', '$users')
@observer
export default class AlertOverview extends React.Component {
  static propTypes = {
    alertModel: PropTypes.object.isRequired, // Must be AlertModel instance
    labelWidth: PropTypes.number
  };

  static defaultProps = {
    labelWidth: 150
  };

  render() {
    const { $auth, $users, alertModel, labelWidth = 150 } = this.props;
    const clearedByUserId = alertModel.clearCause && alertModel.manualClearCause && alertModel.manualClearUserId;
    const clearedByUser = clearedByUserId && $users.collection.get(clearedByUserId)?.get?.('user_full_name');

    return (
      <>
        <LabelValue label="ID" value={<IdTag id={alertModel.id} />} labelWidth={labelWidth} />
        <LabelValue
          label="Severity"
          value={
            <Flex alignItems="center" gap="4px">
              <AlertSeverityBars severity={alertModel.severity} showLabel />
            </Flex>
          }
          labelWidth={labelWidth}
        />
        <LabelValue
          label="Alert State"
          labelWidth={labelWidth}
          value={
            <Flex alignItems="center" gap="4px">
              <AlertStatusTag isActive={alertModel.isActive} />
            </Flex>
          }
        />
        {alertModel.clearCause && !alertModel.unspecifiedClearCause && (
          <LabelValue label="Clear Reason" labelWidth={labelWidth} value={ALERT_CLEAR_REASONS[alertModel.clearCause]} />
        )}
        {(clearedByUserId || clearedByUser) && (
          <LabelValue label="Cleared By" labelWidth={labelWidth} value={clearedByUser || clearedByUserId} />
        )}
        <LabelValue label="Ack State" labelWidth={labelWidth} value={alertModel.ackStateLabel} />
        <LabelValue
          label="Start Time"
          value={
            <>
              {formatDateTime(alertModel.startTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)} ({$auth.userTimezone})
            </>
          }
          labelWidth={labelWidth}
        />
        {alertModel.eventEndTime && (
          <LabelValue
            label="Event End Time"
            value={`${formatDateTime(alertModel.eventEndTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)} (${$auth.userTimezone})`}
            labelWidth={labelWidth}
          />
        )}
        <LabelValue
          label="Clear Time"
          labelWidth={labelWidth}
          value={
            alertModel.endTime
              ? `${formatDateTime(alertModel.endTime, DEFAULT_DATETIME_FORMAT_WITH_SECONDS)}  (${$auth.userTimezone})`
              : 'Currently Active'
          }
        />
        {$auth.canViewCorrelations && alertModel.correlationId && (
          <LabelValue
            label="AI Correlation"
            labelWidth={labelWidth}
            value={
              <Link to={correlationPath(alertModel.correlationId)} monospace title={alertModel.correlationId}>
                {formatDisplayId(alertModel.correlationId)}
              </Link>
            }
          />
        )}
      </>
    );
  }
}
